import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Banknote,
    ArrowLeft,
    Save,
    Building,
    CreditCard,
    Loader2,
    AlertCircle,
    Plus,
    Calendar,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { CreateSettlementData, UpdateSettlementData } from '@/types'

export default function AdminSettlementForm() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = Boolean(id)

    const [formData, setFormData] = useState<CreateSettlementData>({
        batch: 0,
        merchant: 0,
        submerchant: undefined,
        amount_fiat: 0,
        currency: 'USD',
        method: 'bank_transfer',
        reference: '',
        notes: '',
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [merchants, setMerchants] = useState<any[]>([])
    const [batches, setBatches] = useState<any[]>([])

    useEffect(() => {
        if (isEdit && id) {
            fetchSettlementData()
        }
        fetchMerchants()
        fetchBatches()
    }, [id, isEdit])

    const fetchSettlementData = async () => {
        if (!id) return

        try {
            setLoading(true)
            const response = await payoutService.getSettlement(parseInt(id))

            if (response.success) {
                const settlement = response.data
                setFormData({
                    batch: settlement.batch,
                    merchant: settlement.merchant,
                    submerchant: settlement.submerchant || undefined,
                    amount_fiat: Number(settlement.amount_fiat),
                    currency: settlement.currency,
                    method: settlement.method,
                    reference: settlement.reference || '',
                    notes: settlement.notes || '',
                })
            } else {
                setError('Failed to fetch settlement')
            }
        } catch (err) {
            setError('An error occurred while fetching settlement data')
            console.error('Error fetching settlement data:', err)
        } finally {
            setLoading(false)
        }
    }

    const fetchMerchants = async () => {
        try {
            const response = await payoutService.getMerchants()
            if (response.success) {
                setMerchants(response.data.results)
            }
        } catch (err) {
            console.error('Error fetching merchants:', err)
        }
    }

    const fetchBatches = async () => {
        try {
            const response = await payoutService.getPayoutBatches({ page: 1, page_size: 100 })
            if (response.success) {
                setBatches(response.data.results)
            }
        } catch (err) {
            console.error('Error fetching batches:', err)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate required fields
        if (!formData.batch || !formData.merchant || !formData.amount_fiat || !formData.currency || !formData.method) {
            setError('Please fill in all required fields')
            return
        }

        // Validate amount is positive
        if (Number(formData.amount_fiat) <= 0) {
            setError('Settlement amount must be greater than 0')
            return
        }

        // Validate batch and merchant are valid numbers
        if (formData.batch <= 0 || formData.merchant <= 0) {
            setError('Please select valid batch and merchant')
            return
        }

        try {
            setLoading(true)
            setError(null)

            let response
            if (isEdit && id) {
                const updateData: UpdateSettlementData = {
                    amount_fiat: Number(formData.amount_fiat),
                    currency: formData.currency,
                    method: formData.method,
                    reference: formData.reference,
                    notes: formData.notes,
                }
                console.log('🔍 Debug: Updating settlement with data:', updateData)
                response = await payoutService.updateSettlement(parseInt(id), updateData)
            } else {
                // Ensure data types are correct before sending
                const settlementData = {
                    batch: Number(formData.batch),
                    merchant: Number(formData.merchant),
                    submerchant: formData.submerchant ? Number(formData.submerchant) : undefined,
                    amount_fiat: Number(formData.amount_fiat),
                    currency: formData.currency,
                    method: formData.method,
                    reference: formData.reference?.trim() || undefined,
                    notes: formData.notes?.trim() || undefined,
                }

                // Remove undefined values to avoid sending them to the API
                Object.keys(settlementData).forEach(key => {
                    if (settlementData[key as keyof typeof settlementData] === undefined) {
                        delete settlementData[key as keyof typeof settlementData]
                    }
                })

                console.log('🔍 Debug: Creating settlement with data:', settlementData)
                console.log('🔍 Debug: Data types:', {
                    batch: typeof settlementData.batch,
                    merchant: typeof settlementData.merchant,
                    submerchant: typeof settlementData.submerchant,
                    amount_fiat: typeof settlementData.amount_fiat,
                    currency: typeof settlementData.currency,
                    method: typeof settlementData.method,
                })

                response = await payoutService.createSettlement(settlementData)
            }

            if (response.success) {
                navigate('/admin/settlements')
            } else {
                setError(response.message || 'Failed to save settlement')
            }
        } catch (err: any) {
            console.error('Error saving settlement:', err)
            console.error('Error response data:', err.response?.data)
            console.error('Error response status:', err.response?.status)
            console.error('Error response headers:', err.response?.headers)

            // Handle different types of errors
            if (err.response?.data?.message) {
                setError(err.response.data.message)
            } else if (err.response?.data?.error) {
                setError(err.response.data.error)
            } else if (err.response?.data?.detail) {
                setError(err.response.data.detail)
            } else if (err.response?.data?.errors) {
                // Handle validation errors
                const errors = err.response.data.errors
                if (typeof errors === 'object') {
                    const errorMessages = Object.entries(errors).map(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            return `${field}: ${messages.join(', ')}`
                        }
                        return `${field}: ${messages}`
                    })
                    setError(`Validation errors: ${errorMessages.join('; ')}`)
                } else {
                    setError(`Validation error: ${errors}`)
                }
            } else if (err.response?.data?.non_field_errors) {
                setError(err.response.data.non_field_errors.join(', '))
            } else if (err.response?.status === 400) {
                setError('Invalid data provided. Please check your input.')
            } else if (err.response?.status === 401) {
                setError('You are not authorized to perform this action.')
            } else if (err.response?.status === 403) {
                setError('You do not have permission to create settlements.')
            } else if (err.response?.status === 404) {
                setError('The requested resource was not found.')
            } else if (err.response?.status === 500) {
                setError('Server error occurred. Please try again later.')
            } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
                setError('Network error. Please check your connection and try again.')
            } else {
                setError('An unexpected error occurred while saving the settlement')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (field: keyof CreateSettlementData, value: string | number | undefined) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    if (loading && isEdit) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading settlement...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center space-x-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/admin/settlements')}
                            style={{ color: branding.textSecondaryColor }}
                            className="hover:text-white"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold" style={pageStyles.text}>
                                {isEdit ? 'Edit Settlement' : 'Create Settlement'}
                            </h1>
                            <p className="mt-1" style={pageStyles.textSecondary}>
                                {isEdit ? 'Update settlement details' : 'Create a new settlement for a merchant'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: statusColors.error.backgroundColor,
                        borderColor: statusColors.error.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.error.color }} />
                            <p style={{ color: statusColors.error.color }}>{error}</p>
                        </div>
                    </div>
                )}

                {/* Form */}
                <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Payout Batch Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Payout Batch *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                        <Calendar className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                    </div>
                                    <select
                                        value={formData.batch}
                                        onChange={(e) => handleInputChange('batch', parseInt(e.target.value))}
                                        className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor,
                                        }}
                                        required
                                        disabled={isEdit} // Don't allow changing batch in edit mode
                                    >
                                        <option value={0}>Select a payout batch</option>
                                        {batches.filter(batch => batch.status === 'approved').length === 0 ? (
                                            <option value={0} disabled>
                                                No approved batches available
                                            </option>
                                        ) : (
                                            batches
                                                .filter(batch => batch.status === 'approved') // Only show approved batches
                                                .map((batch) => (
                                                    <option key={batch.id} value={batch.id}>
                                                        Batch #{batch.id} - {batch.merchant_name} (${Number(batch.net_amount_fiat).toFixed(2)}) - {batch.status}
                                                    </option>
                                                ))
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Merchant Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Merchant *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                        <Building className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                    </div>
                                    <select
                                        value={formData.merchant}
                                        onChange={(e) => handleInputChange('merchant', parseInt(e.target.value))}
                                        className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor,
                                        }}
                                        required
                                        disabled={isEdit} // Don't allow changing merchant in edit mode
                                    >
                                        <option value={0}>Select a merchant</option>
                                        {merchants.map((merchant) => (
                                            <option key={merchant.id} value={merchant.id}>
                                                {merchant.company_name} ({merchant.name})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Submerchant Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Submerchant (Optional)
                                </label>
                                <select
                                    value={formData.submerchant || ''}
                                    onChange={(e) => handleInputChange('submerchant', e.target.value ? parseInt(e.target.value) : undefined)}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor,
                                    }}
                                >
                                    <option value="">No submerchant</option>
                                    {/* TODO: Populate with actual submerchants from API */}
                                </select>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Settlement Amount *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${statusColors.success.backgroundColor}` }}>
                                        <Banknote className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                    </div>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={Number(formData.amount_fiat)}
                                        onChange={(e) => handleInputChange('amount_fiat', parseFloat(e.target.value))}
                                        className="pl-10 border"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor
                                        }}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Currency */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Currency *
                                </label>
                                <select
                                    value={formData.currency}
                                    onChange={(e) => handleInputChange('currency', e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor,
                                    }}
                                    required
                                >
                                    <option value="USD">USD - US Dollar</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="GBP">GBP - British Pound</option>
                                    <option value="CAD">CAD - Canadian Dollar</option>
                                </select>
                            </div>

                            {/* Payment Method */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Payment Method *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${statusColors.info.backgroundColor}` }}>
                                        <CreditCard className="h-4 w-4" style={{ color: statusColors.info.color }} />
                                    </div>
                                    <select
                                        value={formData.method}
                                        onChange={(e) => handleInputChange('method', e.target.value as 'bank_transfer' | 'crypto' | 'manual')}
                                        className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor,
                                        }}
                                        required
                                    >
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="crypto">Cryptocurrency</option>
                                        <option value="manual">Manual</option>
                                    </select>
                                </div>
                            </div>

                            {/* Reference */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Reference (Optional)
                                </label>
                                <Input
                                    type="text"
                                    value={formData.reference}
                                    onChange={(e) => handleInputChange('reference', e.target.value)}
                                    className="border"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor
                                    }}
                                    placeholder="Transaction reference, bank reference, etc."
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                Notes (Optional)
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                    color: branding.textColor,
                                }}
                                placeholder="Additional notes about this settlement..."
                            />
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-4 pt-6 border-t" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/admin/settlements')}
                                disabled={loading}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center text-white"
                                style={pageStyles.primaryBg}
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : isEdit ? (
                                    <Save className="h-4 w-4 mr-2" />
                                ) : (
                                    <Plus className="h-4 w-4 mr-2" />
                                )}
                                {isEdit ? 'Update Settlement' : 'Create Settlement'}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Help Text */}
                <div className="mt-6 rounded-lg p-4" style={{
                    backgroundColor: `${branding.secondaryColor}20`,
                    borderColor: `${branding.secondaryColor}40`,
                    border: '1px solid'
                }}>
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Banknote className="h-5 w-5" style={{ color: branding.secondaryColor }} />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium" style={{ color: branding.secondaryColor }}>Creating a Settlement</h3>
                            <div className="mt-2 text-sm" style={{ color: `${branding.secondaryColor}CC` }}>
                                <ul className="list-disc list-inside space-y-1">
                                    <li><strong>Important:</strong> Only approved payout batches can be used for settlements</li>
                                    <li>Select the payout batch and merchant for this settlement</li>
                                    <li>Enter the exact amount to be paid out</li>
                                    <li>Choose the appropriate payment method</li>
                                    <li>Add a reference number for tracking purposes</li>
                                    <li>After creation, you can mark the settlement as completed or failed</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Batch Status Info */}
                {batches.filter(batch => batch.status === 'approved').length === 0 && batches.length > 0 && (
                    <div className="mt-4 rounded-lg p-4" style={{
                        backgroundColor: statusColors.warning.backgroundColor,
                        borderColor: statusColors.warning.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5" style={{ color: statusColors.warning.color }} />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium" style={{ color: statusColors.warning.color }}>No Approved Batches Available</h3>
                                <div className="mt-2 text-sm" style={{ color: `${statusColors.warning.color}CC` }}>
                                    <p>You need to approve payout batches before creating settlements. Available batch statuses:</p>
                                    <ul className="list-disc list-inside mt-1 space-y-1">
                                        {Array.from(new Set(batches.map(b => b.status))).map(status => (
                                            <li key={status}>
                                                <span className="capitalize">{status}</span> ({batches.filter(b => b.status === status).length} batches)
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
